import { scopedStoragePath } from './portal';
import { prepareImageForStorageUpload } from './prepareImageForStorageUpload';
import { getDbSupabase } from './supabase';

export const CLIENT_LOGO_BUCKET = 'product-images';

function storageErrorMessage(error: {
  message?: string;
  error?: string;
  statusCode?: number | string;
}): string {
  const parts = [error.message, error.error, String(error.statusCode ?? '')]
    .filter(Boolean)
    .join(' — ');
  if (/no content provided/i.test(parts)) {
    return `${parts} — Mobile photo upload failed to send image bytes. Please try again, or pick a JPG/PNG.`;
  }
  if (/row-level security|policy|not allowed|403|401|InvalidKey|403/i.test(parts)) {
    return `${parts} — Logo Storage 权限不足：在 Supabase 运行 031_storage_logo_upload_fix.sql（需先有 029 桶 product-images），并确认你是该账户负责人或 IT/Admin。`;
  }
  if (/already exists|Duplicate|409/i.test(parts)) {
    return `${parts} — 文件已存在，请刷新页面后重试。`;
  }
  if (/bucket|not found|404/i.test(parts)) {
    return (
      `${parts} — Storage 桶 product-images 不存在。请在 Supabase SQL Editor 运行 ` +
      '029_create_product_images_bucket.sql，或在 Dashboard → Storage → New bucket → 名称填 product-images（Private）。'
    );
  }
  return parts || 'Storage upload failed';
}

export function clientInitials(companyName: string): string {
  return companyName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase() || 'AC';
}

/** Signed or public URL for displaying a stored client logo. */
export async function getClientLogoUrl(logoPath: string): Promise<string | null> {
  const bucket = getDbSupabase().storage.from(CLIENT_LOGO_BUCKET);

  const { data, error } = await bucket.createSignedUrl(logoPath, 60 * 60);
  if (!error && data?.signedUrl) {
    return data.signedUrl;
  }

  const { data: pub } = bucket.getPublicUrl(logoPath);
  return pub.publicUrl || null;
}

export function clientLogoStoragePath(clientId: string, ext: string): string {
  const safeExt = ['png', 'jpg', 'jpeg', 'webp', 'gif'].includes(ext) ? ext : 'png';
  return scopedStoragePath(`client_logos/${clientId}_logo.${safeExt}`);
}

export async function uploadClientLogo(clientId: string, file: File): Promise<string> {
  const prepared = await prepareImageForStorageUpload(file);
  const path = clientLogoStoragePath(clientId, prepared.ext);
  const bucket = getDbSupabase().storage.from(CLIENT_LOGO_BUCKET);

  await bucket.remove([path]);

  const uploadOpts = {
    cacheControl: '3600',
    upsert: true,
    contentType: prepared.contentType,
  } as const;
  let { error } = await bucket.upload(path, prepared.body, uploadOpts);

  if (error && /already exists|Duplicate/i.test(error.message ?? '')) {
    await bucket.remove([path]);
    ({ error } = await bucket.upload(path, prepared.body, uploadOpts));
  }

  if (error) {
    throw new Error(storageErrorMessage(error));
  }
  return path;
}
